import { useEffect, type CSSProperties, type ReactNode } from 'react'
import { DEFAULT_TIME_OFF_COLOR, normalizeHex, withAlpha } from '@diana/shared'

/*
 * Small parts the calendar and its three dialogs share, in the classroom
 * look (Studio-Calendar.png). Kept next to the calendar rather than in
 * classroom/ because nothing else draws hatched time off or a lesson length.
 */

/**
 * Polish counts three ways: 1 godzina, 2–4 godziny, 5+ godzin — and the
 * teens go back to the last form (12 godzin, not 12 godziny).
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const last = n % 10
  const teen = n % 100 >= 12 && n % 100 <= 14
  if (n === 1) return one
  if (!teen && last >= 2 && last <= 4) return few
  return many
}

/** The paper the hatching is drawn on. */
const HATCH_PAPER = '#fbfcfd'

/**
 * „Nie pracuję” is hatched (the mockup's grey stripes). Diana can still give
 * her time off a colour of its own; the stripes then carry a tint of it, and
 * with the default colour they are exactly the mockup's grey.
 */
export function hatch(color: string, small = false): CSSProperties {
  const step = small ? 3 : 6
  const hex = normalizeHex(color) ?? DEFAULT_TIME_OFF_COLOR
  const stripe =
    hex === DEFAULT_TIME_OFF_COLOR ? (small ? '#e9edf2' : '#f3f5f8') : withAlpha(hex, small ? 0.12 : 0.06)
  return {
    backgroundColor: HATCH_PAPER,
    backgroundImage: `repeating-linear-gradient(135deg, ${stripe} 0 ${step}px, transparent ${step}px ${step * 2}px)`,
  }
}

/** A 14px legend swatch. */
export function Swatch({ className = '', style }: { className?: string; style?: CSSProperties }) {
  return <span aria-hidden className={`inline-block h-3.5 w-3.5 shrink-0 rounded ${className}`} style={style} />
}

/** Small buttons for rows and bars (the mockup's 40px, 14px). */
export const SMALL_BTN = 'min-h-10 px-4 text-class-gloss'

/**
 * A short row of mutually exclusive choices — the lesson length. Pressed is
 * the pale-blue „current” fill with a blue edge, the others plain white.
 */
export function Choice<T extends string | number>({
  items,
  value,
  onChange,
  label,
}: {
  items: { value: T; label: ReactNode }[]
  value: T
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {items.map((it) => {
        const on = it.value === value
        return (
          <button
            key={String(it.value)}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(it.value)}
            className={[
              'min-h-9 cursor-pointer rounded-full border-0 px-4 font-class-text text-class-gloss font-semibold transition',
              on
                ? 'bg-class-field-soft text-class-sky-700 shadow-[inset_0_0_0_1px_var(--color-class-sky-700)]'
                : 'bg-class-white text-class-ink-soft shadow-[inset_0_0_0_1px_var(--color-class-sky-200)] hover:text-class-ink',
            ].join(' ')}
          >
            {it.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The calendar's dialogs: a paper sheet over the dimmed screen — from the
 * bottom on a phone, centred on a desk. Escape and a click outside close it.
 */
export function Sheet({
  label,
  title,
  sub,
  onClose,
  children,
}: {
  label: string
  title: ReactNode
  sub?: ReactNode
  onClose: () => void
  children: ReactNode
}) {
  // Escape closes — a modal you can only leave with the mouse is annoying.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-class-ink/40 desk:items-center desk:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="cl-scroll flex max-h-[92dvh] w-full max-w-xl flex-col gap-8 overflow-y-auto rounded-t-class-lg bg-class-paper px-6 pt-6 pb-8 desk:rounded-class-lg desk:px-8 desk:pt-7"
      >
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="cl-heading m-0 first-letter:uppercase">{title}</h2>
            {sub && <p className="m-0 mt-1 text-class-gloss font-semibold text-class-ink-soft">{sub}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="cl-link-quiet -mt-1 -mr-2 flex h-10 w-10 shrink-0 items-center justify-center text-class-heading leading-none"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** One part of a dialog: an 18px head, an optional link on the right, the content. */
export function SheetSection({ title, action, children }: { title: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline gap-3">
        <h3 className="cl-subheading m-0">{title}</h3>
        {action && <div className="ml-auto text-class-gloss">{action}</div>}
      </div>
      {children}
    </section>
  )
}

/** A destructive text button: red words, no box. */
export const DANGER_LINK =
  'cursor-pointer border-0 bg-transparent p-0 font-class-text text-class-gloss font-semibold text-class-wrong hover:text-class-ink disabled:cursor-default disabled:opacity-50'
